import { LogEntry } from '../types'

export interface MessageParts {
  text: string
}

// Each matcher recognises one message family and returns a readable string,
// or null if it does not apply. They are tried in order (specific first); the
// first non-null result wins. The raw description is always kept on the row's
// `title` attribute, so simplifying here never loses information.
//
// NOTE: server-side sorting of the Message column mirrors these rewrites in a
// painless script (MESSAGE_SORT_SCRIPT in elasticQuery.ts). When adding or
// changing a matcher that alters the START of the message, update the script
// too, or sorted order will no longer match the displayed text.
type Matcher = (entry: LogEntry) => string | null

function payloadExceptionMessage(entry: LogEntry): string | null {
  const raw = entry.metadata.payload?.exceptions
  if (!Array.isArray(raw) || raw.length === 0) return null
  const msg = (raw[0] as { Message?: unknown }).Message
  if (typeof msg !== 'string') return null
  return msg.replace(/\.+$/, '').trim()
}

const GATEWAY_RE =
  /^requestId: .*?, previousRequestId: .*?, message: ([\s\S]+)$/
const matchGateway: Matcher = entry => {
  const m = entry.description.match(GATEWAY_RE)
  return m != null ? m[1].trim() : null
}

// LoggingBehavior command outcomes:
//   Processed: "Create" was UNSUCCESSFULL: False on Entity "PlaybackStream" (...)
// The EntityId/TraceId tail and the awkward "UNSUCCESSFULL: <bool>" wording add
// no value on the row, so collapse to "<Command> <Entity> failed|succeeded"
// and, when present, append the real cause from the first exception.
const COMMAND_RE =
  /^Processed: "([^"]+)" was UNSUCCESSFULL: (\w+) on Entity "([^"]+)"/
const matchCommandResult: Matcher = entry => {
  const m = entry.description.match(COMMAND_RE)
  if (m == null) return null
  const [, command, success, entity] = m
  if (success.toLowerCase() === 'true') return `${command} ${entity} succeeded`
  const cause = payloadExceptionMessage(entry)
  return cause != null
    ? `${command} ${entity} failed: ${cause}`
    : `${command} ${entity} failed`
}

// Kafka consumer lifecycle, e.g.
//   Assigned partitions: [CommandMap3D [[0]]]
//   Revoking assignment: [EventNotification [[0]] @Unset [-1001]]
//   Subscribed to: [EventImage]
const ASSIGN_RE = /^Assigned partitions: \[(\w+) \[\[(\d+)\]\]\]$/
const REVOKE_RE = /^Revoking assignment: \[(\w+) \[\[(\d+)\]\]/
const SUBSCRIBE_RE = /^Subscribed to: \[(\w+)\]$/
const matchKafka: Matcher = entry => {
  const d = entry.description
  const assigned = d.match(ASSIGN_RE)
  if (assigned != null)
    return `Assigned partition ${assigned[2]} to ${assigned[1]}`
  const revoked = d.match(REVOKE_RE)
  if (revoked != null)
    return `Revoked partition ${revoked[2]} from ${revoked[1]}`
  const subscribed = d.match(SUBSCRIBE_RE)
  if (subscribed != null) return `Subscribed to ${subscribed[1]}`
  return null
}

const MATCHERS: Matcher[] = [matchGateway, matchCommandResult, matchKafka]

// Cosmetic cleanup applied to every result, recognised or not. SignalR/Serilog
// double-quote names as '"Name"' — reduce that to "Name".
function tidy(text: string): string {
  return text.replace(/'"([^"]*)"'/g, '"$1"').trim()
}

export function formatLogMessage(entry: LogEntry): MessageParts {
  if (entry.audit != null) return { text: tidy(entry.description ?? '') }

  for (const matcher of MATCHERS) {
    const result = matcher(entry)
    if (result != null) return { text: tidy(result) }
  }
  return { text: tidy(entry.description ?? '') }
}
